// Arrival Desk — queue of today's arrivals (left) + step-by-step check-in card (right).
import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, KeyRound, RefreshCw, Search, UserCheck } from 'lucide-react';
import { apiCall } from './_shared';
import ArrivalCard from './ArrivalCard';
import RiskBoard, { RiskBadge } from './RiskBoard';
import { PerkSettings } from './ArrivalPerk';
import { TierBadge } from './TierBadge';

const UNIT_CLS = { clean: 'bg-emerald-100 text-emerald-800', dirty: 'bg-red-100 text-red-800', cleaning: 'bg-sky-100 text-sky-800', to_repair: 'bg-yellow-100 text-yellow-900' };
const todayStr = () => new Date().toISOString().slice(0, 10);

const QueueRow = ({ a, risk, active, onClick }) => {
  const done = Object.values(a.progress).filter(Boolean).length;
  return (
    <button type="button" onClick={onClick} className={`w-full text-left p-3 border-b last:border-0 ${active ? 'bg-orange-50' : 'hover:bg-slate-50'} ${a.checked_in ? 'opacity-60' : ''}`} data-testid={`arrival-row-${a.booking_id}`}>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900 truncate">{a.guest_name}</span>
        {risk && risk.level !== 'low' && !a.checked_in && <RiskBadge r={risk} />}
        <TierBadge loyalty={a.loyalty} testId={`arrival-tier-badge-${a.booking_id}`} />
        {a.returning && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" data-testid={`arrival-returning-${a.booking_id}`}>Returning · {a.past_stays}×</span>}
        {a.perk && <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${a.perk.offered_at ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'}`} data-testid={`arrival-perk-badge-${a.booking_id}`}>{a.perk.offered_at ? 'Perk ✓' : `Perk · ${a.perk.label}`}</span>}
        {a.precheckin_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold">Pre-checked in</span>}
        {a.checked_in && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-semibold">Checked in</span>}
        {a.keys && <KeyRound className="w-3.5 h-3.5 text-emerald-600" title="Keys given" data-testid={`arrival-keys-${a.booking_id}`} />}
        <span className="ml-auto text-xs text-slate-500">{a.eta ? `ETA ${a.eta}` : ''}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-600">
        <span className="truncate max-w-[200px]">{a.room_name}</span>
        {a.unit_label && <span className="font-mono font-semibold">Rm {a.unit_label}</span>}
        {a.unit_status && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${UNIT_CLS[a.unit_status] || 'bg-slate-100'}`} data-testid={`arrival-unit-status-${a.booking_id}`}>{a.unit_status.replace('_', ' ')}</span>}
        {a.balance > 0.009 && <span className="text-red-600 font-semibold">€{Number(a.balance).toFixed(2)} due</span>}
        <span className="ml-auto flex gap-0.5" title="ID · Room · Money · Extras">
          {['id', 'room', 'money', 'extras'].map((s) => <span key={s} className={`w-2 h-2 rounded-full ${a.progress[s] ? 'bg-emerald-500' : 'bg-slate-200'}`} />)}
          <span className="text-[10px] text-slate-400 ml-1">{done}/4</span>
        </span>
      </div>
    </button>
  );
};

const ArrivalDesk = ({ userRole, onNavigate }) => {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [hideDone, setHideDone] = useState(false);
  const [risks, setRisks] = useState({});
  const canEdit = ['super_admin', 'admin', 'manager', 'reception'].includes(userRole);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiCall(`/api/admin/arrival-desk/queue?date_str=${date}`)); } catch (e) { alert(e.message); } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const arrivals = (data?.arrivals || []).filter((a) => (!hideDone || !a.checked_in) && (!q || (a.guest_name || '').toLowerCase().includes(q.toLowerCase()) || (a.unit_label || '').includes(q)));
  const current = (data?.arrivals || []).find((a) => a.booking_id === selected);
  const rows = arrivals.map((a) => <QueueRow key={a.booking_id} a={a} risk={risks[a.booking_id]} active={selected === a.booking_id} onClick={() => setSelected(a.booking_id)} />);

  return (
    <div className="space-y-4" data-testid="arrival-desk">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><UserCheck className="w-6 h-6 text-orange-500" /> Arrival Desk</h2>
        <span className="text-sm text-slate-500">Work through arrivals one guest at a time.</span>
        <span className="flex-1" />
        <PerkSettings canEdit={['super_admin', 'admin', 'manager'].includes(userRole)} />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="arrival-date" />
        <button type="button" onClick={load} className="inline-flex items-center gap-1 text-sm border rounded-lg px-3 py-1.5 hover:bg-slate-50" data-testid="arrival-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>
      <RiskBoard date={date} canEdit={canEdit} onRisks={setRisks} />
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="arrival-kpis">
          <div className="bg-white border rounded-xl p-3"><div className="text-[10px] uppercase text-slate-500 font-semibold">Arrivals</div><div className="text-2xl font-bold">{data.count}</div></div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3"><div className="text-[10px] uppercase text-emerald-700 font-semibold">Checked in</div><div className="text-2xl font-bold text-emerald-700">{data.checked_in}</div></div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3"><div className="text-[10px] uppercase text-indigo-700 font-semibold">Pre-checked in online</div><div className="text-2xl font-bold text-indigo-700">{data.pre_checked_in}</div></div>
          <div className={`${data.rooms_not_ready ? 'bg-red-50 border-red-200' : 'bg-white'} border rounded-xl p-3`}><div className="text-[10px] uppercase text-red-700 font-semibold">Rooms not ready</div><div className="text-2xl font-bold text-red-700" data-testid="arrival-not-ready">{data.rooms_not_ready}</div></div>
        </div>
      )}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white border rounded-xl overflow-hidden">
          <div className="p-3 border-b flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Guest or room…" className="flex-1 text-sm outline-none" data-testid="arrival-search" />
            <label className="text-xs flex items-center gap-1 text-slate-600"><input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} /> hide checked-in</label>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="arrival-queue">
            {rows.length ? rows : <div className="p-8 text-center text-slate-500 text-sm">{loading ? 'Loading…' : 'No arrivals for this date.'}</div>}
          </div>
        </div>
        <div className="lg:col-span-3 bg-white border rounded-xl p-4 min-h-[300px]">
          {current ? <ArrivalCard arrival={current} canEdit={canEdit} onChanged={load} onNavigate={onNavigate} />
            : <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm gap-2 py-16"><ClipboardCheck className="w-8 h-8" /> Pick a guest from the queue to start the check-in</div>}
        </div>
      </div>
    </div>
  );
};

export default ArrivalDesk;
